import React from 'react';
import { IconProperties } from '../icons.properties';

// ─────────────────────────────────────────────────────────────────────
// <Icon name="check" size={16} /> — single entry point for UI icons.
//
// Renders a lucide-react component looked up by semantic name from the
// central registry (src/icons.properties.js). This replaces the emoji
// "icon" tags that used to be scattered through JSON data and JSX.
//
// Fail-safe: if `name` is unknown it renders nothing (never crashes the
// build), so a missing mapping degrades gracefully instead of throwing.
//
// Props:
//   name    — registry key (e.g. "view", "warning", "completed")
//   size    — px size (default 16)
//   color   — stroke color (defaults to currentColor → inherits text color)
//   strokeWidth, className, style, ...rest — forwarded to the svg
// ─────────────────────────────────────────────────────────────────────
export function Icon({ name, size = 16, color, strokeWidth = 2, style, className, ...rest }) {
  const Cmp = IconProperties[name];
  if (!Cmp) return null;
  return (
    <Cmp
      size={size}
      color={color}
      strokeWidth={strokeWidth}
      className={className}
      style={{ flexShrink: 0, verticalAlign: 'middle', ...style }}
      aria-hidden="true"
      {...rest}
    />
  );
}

// Inline helper: an icon vertically aligned next to text in a flex row.
export function InlineIcon({ name, size = 14, gap = 6, children, style, ...rest }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap, ...style }}>
      <Icon name={name} size={size} {...rest} />
      {children}
    </span>
  );
}

export default Icon;
